/**
 * Admin AI Test Page - Display GPT result data
 * Shows AI analysis results from the gpt_result table
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  VStack,
  HStack,
  Text,
  Badge,
  SimpleGrid,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Icon,
  Box,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Textarea,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';
import {
  FiSearch,
  FiFilter,
  FiClock,
  FiCpu,
  FiFileText,
  FiDatabase,
  FiZap,
} from 'react-icons/fi';

import {
  AdminLayout,
  Card,
  StatsCard,
  DataTable,
  Button,
} from '@grcs/components';
import { useData } from '../../context/DataContext';

interface GPTResult {
  id: string; // UUID
  created_at: string;
  input: string;
  output: string;
  appeared_words: string;
  must_words: any; // JSON
  comment: string;
  unknown_words: string; // Changed from any to string
  vocabulary: any; // JSON
  log_id: number;
  deleted: boolean;
  known_hanzi: string; // TEXT
  unknown_hanzi_overall: string; // TEXT
  unknown_hanzi_usage: string; // TEXT
}

export const AdminAITestPageEnhanced: React.FC = () => {
  const { supabase } = useData();

  // State
  const [gptResults, setGPTResults] = useState<GPTResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'created_at' | 'input' | 'output'>(
    'created_at',
  );
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedResult, setSelectedResult] = useState<GPTResult | null>(null);

  // Modal state
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Theme colors
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Load GPT results
  useEffect(() => {
    loadGPTResults();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadGPTResults = async () => {
    setLoading(true);
    try {
      console.log('Loading GPT results...');
      const { data, error } = await supabase
        .from('gpt_result')
        .select('*')
        .order('created_at', { ascending: false });

      console.log('GPT results response:', { data, error });

      if (error) {
        console.error('Error loading GPT results:', error);
      } else {
        console.log(
          'GPT results loaded successfully:',
          data?.length || 0,
          'records',
        );
        setGPTResults(data || []);
      }
    } catch (error) {
      console.error('Error loading GPT results:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let filtered = gptResults.filter(
      (result) =>
        result.input.toLowerCase().includes(searchTerm.toLowerCase()) ||
        result.output.toLowerCase().includes(searchTerm.toLowerCase()) ||
        result.appeared_words?.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    filtered.sort((a, b) => {
      if (sortBy === 'created_at') {
        const aValue = new Date(a[sortBy]).getTime();
        const bValue = new Date(b[sortBy]).getTime();

        if (sortOrder === 'asc') {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        }
      } else {
        const aValue = String(a[sortBy] || '').toLowerCase();
        const bValue = String(b[sortBy] || '').toLowerCase();

        if (sortOrder === 'asc') {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        }
      }
    });

    return filtered;
  }, [gptResults, searchTerm, sortBy, sortOrder]);

  // Stats calculations
  const stats = useMemo(() => {
    return {
      totalResults: gptResults.length,
      avgInputLength:
        gptResults.length > 0
          ? Math.round(
              gptResults.reduce((sum, r) => sum + (r.input?.length || 0), 0) /
                gptResults.length,
            )
          : 0,
      avgOutputLength:
        gptResults.length > 0
          ? Math.round(
              gptResults.reduce((sum, r) => sum + (r.output?.length || 0), 0) /
                gptResults.length,
            )
          : 0,
      recentResults: gptResults.filter((r) => {
        const resultDate = new Date(r.created_at);
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return resultDate >= weekAgo;
      }).length,
    };
  }, [gptResults]);

  const truncateText = (text: string, maxLength: number = 100) => {
    if (!text) return '';
    return text.length > maxLength
      ? text.substring(0, maxLength) + '...'
      : text;
  };

  const formatJSON = (data: any) => {
    if (!data) return 'N/A';
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  };

  const handleViewDetails = (row: GPTResult) => {
    setSelectedResult(row);
    onOpen();
  };

  const tableColumns = [
    {
      header: 'Created',
      key: 'created_at',
      width: '140px',
      render: (row: GPTResult, value: any) => (
        <VStack spacing={0} align="start">
          <Text fontSize="sm" fontWeight="medium">
            {value ? new Date(value).toLocaleDateString() : 'N/A'}
          </Text>
          <Text fontSize="xs" color="gray.500">
            {value ? new Date(value).toLocaleTimeString() : 'N/A'}
          </Text>
        </VStack>
      ),
    },
    {
      header: 'Log ID',
      key: 'log_id',
      width: '80px',
      render: (row: GPTResult, value: any) => (
        <Badge colorScheme="blue" variant="outline">
          {value}
        </Badge>
      ),
    },
    {
      header: 'Input',
      key: 'input',
      render: (row: GPTResult, value: any) => (
        <Box maxW="250px">
          <Text fontSize="sm" noOfLines={2}>
            {truncateText(value || '')}
          </Text>
        </Box>
      ),
    },
    {
      header: 'Output',
      key: 'output',
      render: (row: GPTResult, value: any) => (
        <Box maxW="250px">
          <Text fontSize="sm" noOfLines={2}>
            {truncateText(value || '')}
          </Text>
        </Box>
      ),
    },
    {
      header: 'Must Words',
      key: 'must_words',
      width: '150px',
      render: (row: GPTResult, value: any) => (
        <Box maxW="150px">
          <Text fontSize="xs" noOfLines={2} color="blue.600">
            {value ? truncateText(JSON.stringify(value), 50) : 'N/A'}
          </Text>
        </Box>
      ),
    },
    {
      header: 'Comment',
      key: 'comment',
      render: (row: GPTResult, value: any) => (
        <Box maxW="180px">
          <Text fontSize="sm" noOfLines={1}>
            {value ? truncateText(value, 50) : 'No comment'}
          </Text>
        </Box>
      ),
    },
    {
      header: 'Appeared Words',
      key: 'appeared_words',
      render: (row: GPTResult, value: any) => (
        <Badge colorScheme="green" variant="subtle">
          {truncateText(value || 'N/A', 30)}
        </Badge>
      ),
    },
    {
      header: 'Unknown Words',
      key: 'unknown_words',
      render: (row: GPTResult, value: any) => (
        <Box maxW="150px">
          <Text fontSize="xs" noOfLines={2} color="red.600">
            {truncateText(value || 'N/A', 50)}
          </Text>
        </Box>
      ),
    },
    {
      header: 'Vocabulary',
      key: 'vocabulary',
      width: '150px',
      render: (row: GPTResult, value: any) => (
        <Box maxW="150px">
          <Text fontSize="xs" noOfLines={2} color="purple.600">
            {value ? truncateText(JSON.stringify(value), 50) : 'N/A'}
          </Text>
        </Box>
      ),
    },
    {
      header: 'Known Hanzi',
      key: 'known_hanzi',
      render: (row: GPTResult, value: any) => (
        <Box maxW="120px">
          <Text fontSize="xs" noOfLines={2} color="green.600">
            {truncateText(value || 'N/A', 40)}
          </Text>
        </Box>
      ),
    },
    {
      header: 'Unknown Hanzi Overall',
      key: 'unknown_hanzi_overall',
      render: (row: GPTResult, value: any) => (
        <Box maxW="150px">
          <Text fontSize="xs" noOfLines={2} color="orange.600">
            {truncateText(value || 'N/A', 50)}
          </Text>
        </Box>
      ),
    },
    {
      header: 'Unknown Hanzi Usage',
      key: 'unknown_hanzi_usage',
      render: (row: GPTResult, value: any) => (
        <Box maxW="150px">
          <Text fontSize="xs" noOfLines={2} color="teal.600">
            {truncateText(value || 'N/A', 50)}
          </Text>
        </Box>
      ),
    },
    {
      header: 'Deleted',
      key: 'deleted',
      width: '80px',
      render: (row: GPTResult, value: any) => (
        <Badge colorScheme={value ? 'red' : 'green'} variant="solid">
          {value ? 'Yes' : 'No'}
        </Badge>
      ),
    },
    {
      header: 'Actions',
      key: 'actions',
      width: '120px',
      render: (row: GPTResult) => (
        <Button
          size="sm"
          colorScheme="blue"
          variant="outline"
          onClick={() => handleViewDetails(row)}
        >
          View Details
        </Button>
      ),
    },
  ];

  return (
    <AdminLayout>
      <VStack spacing={6} align="stretch" w="100%">
        {/* Page Header */}
        <HStack justify="space-between">
          <VStack spacing={1} align="start">
            <HStack spacing={2}>
              <Icon as={FiZap} color="purple.500" boxSize={6} />
              <Text fontSize="2xl" fontWeight="bold">
                AI Test Results
              </Text>
            </HStack>
            <Text color="gray.600">
              GPT analysis results and AI testing data
            </Text>
          </VStack>
          <Button
            leftIcon={<FiDatabase />}
            colorScheme="purple"
            onClick={loadGPTResults}
            isLoading={loading}
          >
            Refresh Data
          </Button>
        </HStack>

        {/* Stats Cards */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
          <StatsCard
            title="Total Results"
            value={stats.totalResults}
            icon={FiFileText}
            color="blue"
          />
          <StatsCard
            title="Recent Results"
            value={stats.recentResults}
            subtitle="Last 7 days"
            icon={FiClock}
            color="green"
          />
          <StatsCard
            title="Avg Input Length"
            value={stats.avgInputLength}
            subtitle="characters"
            icon={FiCpu}
            color="purple"
          />
          <StatsCard
            title="Avg Output Length"
            value={stats.avgOutputLength}
            subtitle="characters"
            icon={FiFileText}
            color="orange"
          />
        </SimpleGrid>

        {/* Filters */}
        <Card bg={cardBg} borderColor={borderColor}>
          <HStack spacing={4} wrap="wrap">
            <InputGroup maxW="300px">
              <InputLeftElement pointerEvents="none">
                <Icon as={FiSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search input, output, or words..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>

            <HStack spacing={2}>
              <Icon as={FiFilter} color="gray.400" />
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                maxW="150px"
              >
                <option value="created_at">Created Date</option>
                <option value="input">Input</option>
                <option value="output">Output</option>
              </Select>
            </HStack>

            <Select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as any)}
              maxW="120px"
            >
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </Select>
          </HStack>
        </Card>

        {/* Results Table */}
        <Card bg={cardBg} borderColor={borderColor}>
          <Box overflowX="auto">
            <DataTable
              data={filteredAndSortedData}
              columns={tableColumns}
              loading={loading}
              emptyMessage={
                loading
                  ? 'Loading GPT results...'
                  : 'No GPT results found. Check console for errors.'
              }
              pagination={{
                currentPage,
                totalPages: Math.ceil(filteredAndSortedData.length / pageSize),
                pageSize,
                totalItems: filteredAndSortedData.length,
                onPageChange: setCurrentPage,
                onPageSizeChange: setPageSize,
              }}
            />
          </Box>
        </Card>
      </VStack>

      {/* View Details Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            GPT Result Details
            {selectedResult && (
              <Text fontSize="sm" color="gray.500" fontWeight="normal">
                Created: {new Date(selectedResult.created_at).toLocaleString()}
              </Text>
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedResult && (
              <VStack spacing={6} align="stretch">
                {/* Basic Info */}
                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={3}>
                  <Badge colorScheme="blue" p={2}>
                    <Text fontSize="xs">
                      <strong>ID:</strong> {selectedResult.id.substring(0, 8)}
                      ...
                    </Text>
                  </Badge>
                  <Badge colorScheme="purple" p={2}>
                    <Text fontSize="xs">
                      <strong>Log ID:</strong> {selectedResult.log_id}
                    </Text>
                  </Badge>
                  <Badge colorScheme="green" p={2}>
                    <Text fontSize="xs">
                      <strong>Words:</strong>{' '}
                      {selectedResult.appeared_words || 'N/A'}
                    </Text>
                  </Badge>
                  <Badge
                    colorScheme={selectedResult.deleted ? 'red' : 'gray'}
                    p={2}
                  >
                    <Text fontSize="xs">
                      <strong>Status:</strong>{' '}
                      {selectedResult.deleted ? 'Deleted' : 'Active'}
                    </Text>
                  </Badge>
                </SimpleGrid>

                {/* Comment Section */}
                {selectedResult.comment && (
                  <Box
                    p={4}
                    bg="blue.50"
                    borderRadius="md"
                    borderLeft="4px"
                    borderLeftColor="blue.500"
                  >
                    <Text fontWeight="semibold" color="blue.700" mb={2}>
                      Comment:
                    </Text>
                    <Text color="blue.600">{selectedResult.comment}</Text>
                  </Box>
                )}

                {/* Text Content */}
                <Accordion allowToggle defaultIndex={[0, 1]}>
                  <AccordionItem>
                    <AccordionButton>
                      <Box flex="1" textAlign="left">
                        <Text fontWeight="semibold">Input Text</Text>
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel>
                      <Textarea
                        value={selectedResult.input || ''}
                        readOnly
                        minH="150px"
                        bg="gray.50"
                        resize="vertical"
                      />
                    </AccordionPanel>
                  </AccordionItem>

                  <AccordionItem>
                    <AccordionButton>
                      <Box flex="1" textAlign="left">
                        <Text fontWeight="semibold">Output Text</Text>
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel>
                      <Textarea
                        value={selectedResult.output || ''}
                        readOnly
                        minH="150px"
                        bg="gray.50"
                        resize="vertical"
                      />
                    </AccordionPanel>
                  </AccordionItem>

                  <AccordionItem>
                    <AccordionButton>
                      <Box flex="1" textAlign="left">
                        <Text fontWeight="semibold">Analysis Data</Text>
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        {/* JSON Fields */}
                        <Box>
                          <Text fontWeight="semibold" mb={2} color="blue.600">
                            Must Words (JSON):
                          </Text>
                          <Textarea
                            value={formatJSON(selectedResult.must_words)}
                            readOnly
                            minH="120px"
                            bg="blue.50"
                            fontSize="sm"
                            fontFamily="mono"
                            resize="vertical"
                          />
                        </Box>
                        <Box>
                          <Text fontWeight="semibold" mb={2} color="purple.600">
                            Vocabulary (JSON):
                          </Text>
                          <Textarea
                            value={formatJSON(selectedResult.vocabulary)}
                            readOnly
                            minH="120px"
                            bg="purple.50"
                            fontSize="sm"
                            fontFamily="mono"
                            resize="vertical"
                          />
                        </Box>

                        {/* Text Fields */}
                        <Box>
                          <Text fontWeight="semibold" mb={2} color="red.600">
                            Unknown Words:
                          </Text>
                          <Textarea
                            value={selectedResult.unknown_words || ''}
                            readOnly
                            minH="120px"
                            bg="red.50"
                            fontSize="sm"
                            resize="vertical"
                          />
                        </Box>
                        <Box>
                          <Text fontWeight="semibold" mb={2} color="green.600">
                            Known Hanzi:
                          </Text>
                          <Textarea
                            value={selectedResult.known_hanzi || ''}
                            readOnly
                            minH="120px"
                            bg="green.50"
                            fontSize="sm"
                            resize="vertical"
                          />
                        </Box>
                        <Box>
                          <Text fontWeight="semibold" mb={2} color="orange.600">
                            Unknown Hanzi Overall:
                          </Text>
                          <Textarea
                            value={selectedResult.unknown_hanzi_overall || ''}
                            readOnly
                            minH="120px"
                            bg="orange.50"
                            fontSize="sm"
                            resize="vertical"
                          />
                        </Box>
                        <Box>
                          <Text fontWeight="semibold" mb={2} color="teal.600">
                            Unknown Hanzi Usage:
                          </Text>
                          <Textarea
                            value={selectedResult.unknown_hanzi_usage || ''}
                            readOnly
                            minH="120px"
                            bg="teal.50"
                            fontSize="sm"
                            resize="vertical"
                          />
                        </Box>
                      </SimpleGrid>
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AdminLayout>
  );
};
